import { InvoiceStatus } from '@admiin-com/ds-graphql';
import { WBBox, WBFlex, WBForm, WBTextFieldV2 } from '@admiin-com/ds-web';
import { styled } from '@mui/material';
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { InvoiceNumberEdit } from './components/InvoiceNumberEdit';
import AutoCompleteLookupV2 from '../AutoCompleteLookup/AutoCompleteLookupV2';
import TaskUpload from './components/TaskUpload';
import { InvoiceCreateButtons } from '../InvoiceCreateForm/InvoiceCreateButtons';
import { useFromToTypeFromTaskDirection } from '../../pages/TaskCreation/useFromToTypeFromTaskDirection';
import React from 'react';
import { useTaskCreationContext } from '../../pages/TaskCreation';
import useSelectedFirm from '../../hooks/useSelectedFirm/useSelectedFirm';
import { useDefaultValueForFromTo } from '../../features/providers/TaskCreationProvider/TaskCreationProvider';
import DueDateSelectorV2 from '../DueDateSelector/DueDateSelectorV2';
import { TASK_FORM_FIELDS } from '../../helpers/form';
import { InvoicePaymentFrequency } from './components/InvoicePaymentFrequency';
import { InvoiceType } from './components/InvoiceType';
import { useSelectedEntity } from '../../hooks/useSelectedEntity/useSelectedEntity';

interface Props {
  handleNext: () => void;
  onDraft: () => Promise<void>;
  onPreview: () => void;
  isDraft?: boolean;
}

export const InvoiceDetail = ({
  onDraft,
  onPreview,
  handleNext,
  isDraft,
}: Props) => {
  const { t } = useTranslation();
  const { entity } = useSelectedEntity();
  const invoiceStatus = useWatch({ name: 'invoiceStatus' });
  const { client } = useTaskCreationContext();

  const methods = useFormContext();
  const {
    control,
    handleSubmit,
    watch,
    formState: { errors, isValid },
  } = methods;
  const disabled = !isValid || Object.keys(errors).length > 0;

  const inputs = React.useMemo(
    () => ({
      from: {
        ...TASK_FORM_FIELDS.from,
        defaultValue: entity,
      },
      to: {
        ...TASK_FORM_FIELDS.to,
        defaultValue: client ? client : null,
      },
      paymentTypes: {
        ...TASK_FORM_FIELDS.paymentTypes,
      },
      dueAt: {
        ...TASK_FORM_FIELDS.dueAt,
      },
      paymentAt: {
        ...TASK_FORM_FIELDS.paymentAt,
      },
      firstPaymentAt: {
        ...TASK_FORM_FIELDS.firstPaymentAt,
      },
      referenceNumber: {
        ...TASK_FORM_FIELDS.reference,
      },
      documents: {
        ...TASK_FORM_FIELDS.supportDocuments,
      },
      note: {
        ...TASK_FORM_FIELDS.note,
        placeholder: `${t(
          invoiceStatus === InvoiceStatus.INVOICE
            ? 'invoiceCommentsPlaceholder'
            : 'quoteCommentsPlaceholder',
          { ns: 'taskbox' }
        )}`,
      },
      issuedAt: {
        ...TASK_FORM_FIELDS.issuedAt,
      },
    }),
    [t, entity, invoiceStatus, client]
  );
  const onSubmit = () => {
    console.log('submit');
  };

  const entityId = watch('from')?.id;
  const { toType, setToType } = useFromToTypeFromTaskDirection();

  const { selectedFirm } = useSelectedFirm();

  React.useEffect(() => {
    if (client)
      if (entityId && entityId === selectedFirm?.id) {
        methods.setValue('to', client);
        setToType('Entity');
      }
  }, [entityId, selectedFirm, client]);

  return (
    <Container onSubmit={handleSubmit(onSubmit)}>
      <FormContainer>
        <InvoiceType isDraft={isDraft} />
        <WBBox mt={3}>
          <InvoiceNumberEdit />
        </WBBox>
        <WBFlex flexDirection={['column', 'row']} gap={[0]}>
          <Controller
            control={control}
            name={inputs.to.name}
            defaultValue={inputs.to.defaultValue}
            rules={inputs.to.rules}
            render={({ field, formState: { errors } }) => (
              <WBBox width={['100%', '50%']} pr={[0, 2]}>
                <AutoCompleteLookupV2
                  {...field}
                  noPopupIcon
                  label={inputs.to.label}
                  placeholder={inputs.to.placeholder}
                  type={toType}
                  entityId={entityId}
                  helperText={(errors[field.name]?.message as string) ?? ''}
                  error={!!errors[field.name]}
                />
              </WBBox>
            )}
          />
          <Controller
            control={control}
            name={inputs.from.name}
            rules={inputs.from.rules}
            defaultValue={inputs.from.defaultValue}
            render={({ field, formState: { errors } }) => (
              <WBBox width={['100%', '50%']} pl={[0, 2]}>
                <div />
                {/* <AutoCompleteLookupV2
                  {...field}
                  noPopupIcon
                  additonalOptions={additionalFirmOption}
                  label={inputs.from.label}
                  placeholder={inputs.from.placeholder}
                  type={fromType}
                  helperText={(errors[field.name]?.message as string) ?? ''}
                  error={!!errors[field.name]}
                /> */}
              </WBBox>
            )}
          />
        </WBFlex>
        <WBFlex flexDirection={['column', 'row']} gap={[0, 4]}>
          <Controller
            control={control}
            name={inputs.issuedAt.name}
            rules={inputs.issuedAt.rules}
            defaultValue={inputs.issuedAt.defaultValue}
            render={({ field, formState: { errors } }) => (
              <DueDateSelectorV2
                {...field}
                type={inputs.issuedAt.type}
                placeholder={inputs.issuedAt.placeholder}
                label={inputs.issuedAt.label}
                fullWidth
                helperText={(errors[field.name]?.message as string) ?? ''}
                error={!!errors[field.name]}
              />
            )}
          />
          <Controller
            control={control}
            name={inputs.dueAt.name}
            rules={inputs.dueAt.rules}
            defaultValue={inputs.dueAt.defaultValue}
            render={({ field, formState: { errors } }) => (
              <DueDateSelectorV2
                {...field}
                type={inputs.dueAt.type}
                placeholder={inputs.dueAt.placeholder}
                label={inputs.dueAt.label}
                fullWidth
                helperText={(errors[field.name]?.message as string) ?? ''}
                error={!!errors[field.name]}
              />
            )}
          />
        </WBFlex>
        <InvoicePaymentFrequency />

        <WBBox mt={2}>
          <Controller
            rules={inputs.documents.rules}
            control={control}
            name={inputs.documents.name}
            render={({ field }) => (
              <TaskUpload
                {...field}
                label={inputs.documents.label}
                includeNameInKey
              />
            )}
          />
        </WBBox>
        <Controller
          control={control}
          name={'notes' as const}
          rules={inputs.note.rules}
          render={({ field }) => (
            <WBTextFieldV2
              {...field}
              type={inputs.note.type}
              placeholder={inputs.note.placeholder}
              label={inputs.note.label}
              key={field.name}
              multiline
              fullWidth
            />
          )}
        />
      </FormContainer>

      <InvoiceCreateButtons
        onDraft={onDraft}
        onPreview={onPreview}
        handleNext={handleNext}
        isValid={!disabled}
        disableDraft={isDraft}
      />
    </Container>
  );
};

const Container = styled(WBForm)(({ theme }) => ({
  flexDirection: 'column',
  flex: 1,
  width: '100%',
  alignItems: 'end',
  backgroundColor: theme.palette.background.default,
}));

const FormContainer = styled(WBBox)(({ theme }) => ({
  background: theme.palette.background.paper,
  padding: theme.spacing(5),
  paddingTop: theme.spacing(4),
  paddingBottom: theme.spacing(4),
  flex: 1,
  width: '100%',
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(2),
    paddingTop: theme.spacing(3),
  },
}));
